import React from 'react';
import cx from 'classnames';
import {Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {SavedSearch} from '../../../../../../types';
import {useHover} from '../../../../../../../../hooks/useHover';
import {ModernTooltip} from '../../../../../../../../components/Common/Tooltip/ModernTooltip';
import {DropdownBaseItem} from './DropdownItem';
import styles from '../styles.module.scss';
import {texts} from '../../../texts';

interface SavedSearchProp {
    item: SavedSearch;
    isFocused: boolean;
    isActive?: boolean;
    itemBorder?: boolean;
    componentClasses?: string;
    withShortcut?: boolean;
    onExecute?: (id: string) => void;
    onEdit?: (id: string) => void;
    onToggleDefault?: (id: string) => void;
    onDelete?: (id: string) => void;
}

const SavedSearchItem = ({
    item,
    componentClasses,
    withShortcut,
    onExecute,
    onDelete,
    onEdit,
    onToggleDefault,
    isFocused,
    itemBorder,
    isActive
}: SavedSearchProp) => {
    const {setRef, isHovering} = useHover();

    const handleActionClick = (e: MouseEvent, action: (id: string) => void) => {
        e.stopPropagation();
        action(item.id);
    };

    return (
        <DropdownBaseItem
            ref={setRef}
            onClick={() => onExecute(item.id)}
            componentClasses={componentClasses}
            isFocused={isFocused}
            itemBorder={itemBorder}
            isActive={isActive}
        >
            <div className={styles.itemContainer}>
                <Text skin="paragraph-2" componentClasses={styles.text}>
                    {item.name}
                </Text>

                <div className={cx(styles.actionsContainer)}>
                    {isHovering ? (
                        <>
                            {onToggleDefault && (
                                <ModernTooltip
                                    position="top"
                                    tooltip={
                                        <Text skin="paragraph-2">
                                            {item.isDefault ? texts.unsetDefault : texts.setDefault}
                                        </Text>
                                    }
                                >
                                    <Icon
                                        componentClasses={styles.hover}
                                        onClick={e => handleActionClick(e, onToggleDefault)}
                                        icon={
                                            item.isDefault
                                                ? PriorityIcon.starFilled
                                                : PriorityIcon.starEmpty
                                        }
                                        iconColor={'primary-700'}
                                    />
                                </ModernTooltip>
                            )}
                            {onEdit && (
                                <ModernTooltip tooltip={texts.editSearchTooltip} position="top">
                                    <Icon
                                        componentClasses={styles.hover}
                                        iconColor="primary-700"
                                        onClick={e => handleActionClick(e, onEdit)}
                                        icon={PriorityIcon.editFill}
                                    />
                                </ModernTooltip>
                            )}
                            {onDelete && (
                                <Icon
                                    componentClasses={styles.hover}
                                    iconColor="primary-700"
                                    onClick={e => handleActionClick(e, onDelete)}
                                    icon={PriorityIcon.delete}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {item.isDefault && (
                                <Icon icon={PriorityIcon.starFilled} iconColor={'primary-500'} />
                            )}
                            {withShortcut && (
                                <Text
                                    componentClasses={styles.shortKey}
                                    wrap={false}
                                    textColor="secondary-400"
                                    skin="label-1"
                                >
                                    {`[ Ctrl + ${item.index} ]`}
                                </Text>
                            )}
                        </>
                    )}
                </div>
            </div>
        </DropdownBaseItem>
    );
};

export default SavedSearchItem;
